import {
  resolveFileLinkMeta,
  useSDK,
  type File,
  type FileLinkResolution,
  type LittlebirdSDK,
} from '@littlebird/sdk';
import { useEffect, useState } from 'react';

import { logError } from '@/lib/errorLogging';

import { useHydratedFile } from './useHydratedFile';

export type FileLinkState =
  | { status: 'loading'; file: undefined }
  | { status: 'ready'; file: File }
  | { status: 'unavailable'; file: undefined };

type TokenResolution =
  | { sdk: LittlebirdSDK; token: string; result: { status: 'loading' } }
  | { sdk: LittlebirdSDK; token: string; result: FileLinkResolution };

export function useFileLink(token: string): FileLinkState {
  const sdk = useSDK();
  const [resolution, setResolution] = useState<TokenResolution>({
    sdk,
    token,
    result: { status: 'loading' },
  });
  const currentResult: FileLinkResolution | { status: 'loading' } =
    resolution.sdk === sdk && resolution.token === token
      ? resolution.result
      : { status: 'loading' };
  const hydrated = useHydratedFile(
    currentResult.status === 'ready' ? currentResult.fileId : undefined
  );

  useEffect(() => {
    let cancelled = false;
    void resolveFileLinkMeta(sdk, token).then(
      (result) => {
        if (!cancelled) setResolution({ sdk, token, result });
      },
      (error: unknown) => {
        logError(error, 'Failed to resolve file link');
        if (!cancelled) {
          setResolution({
            sdk,
            token,
            result: { status: 'unavailable' },
          });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [sdk, token]);

  if (currentResult.status !== 'ready') {
    return currentResult.status === 'loading'
      ? { status: 'loading', file: undefined }
      : { status: 'unavailable', file: undefined };
  }
  if (hydrated.status === 'ready') return hydrated;
  return hydrated.status === 'unavailable'
    ? { status: 'unavailable', file: undefined }
    : { status: 'loading', file: undefined };
}
